import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Send, Clock } from 'lucide-react';
import API from '../../lib/api';

// ── Circular SVG progress indicator ───────────────────────────────────────
function CircleProgress({ pct, size = 160, stroke = 10, color = '#6C63FF' }) {
  const r   = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ * (1 - (pct || 0) / 100);

  return (
    <div className="relative flex-shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#E8E6F8" strokeWidth={stroke} />
        <motion.circle
          cx={size / 2} cy={size / 2} r={r} fill="none"
          stroke={color} strokeWidth={stroke} strokeLinecap="round"
          strokeDasharray={circ}
          initial={{ strokeDashoffset: circ }}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 1.2, ease: 'easeOut' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <motion.span
          key={pct}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="font-heading font-black text-[#6C63FF] leading-none"
          style={{ fontSize: size * 0.22 }}
        >
          {pct ?? 0}%
        </motion.span>
      </div>
    </div>
  );
}

function statusLabel(pct) {
  if (pct == null)  return 'Not Started';
  if (pct === 0)    return 'Under Review';
  if (pct < 100)    return 'In Progress';
  return 'Complete';
}

function statusColor(pct) {
  if (pct == null || pct === 0) return 'bg-gray-100 text-gray-500';
  if (pct < 100)                return 'bg-orange-100 text-orange-600';
  return 'bg-green-100 text-green-700';
}

// ── Client Preview (mirrors what user sees on ComputationPage) ─────────────
function ClientPreview({ pct, msg }) {
  const circ = 2 * Math.PI * 38;
  const offset = circ * (1 - (pct || 0) / 100);
  return (
    <div className="bg-[#F5F4FB] border-2 border-dashed border-[#6C63FF]/20 rounded-2xl p-4">
      <p className="text-[10px] font-body font-semibold text-[#6C63FF] uppercase tracking-widest mb-3">
        Preview — what client will see
      </p>
      <div className="bg-white rounded-xl p-4 flex flex-col items-center text-center shadow-sm border border-[#E8E6F8]">
        <div className="relative w-24 h-24 mb-3">
          <svg className="w-full h-full -rotate-90" viewBox="0 0 84 84">
            <circle cx="42" cy="42" r="38" fill="none" stroke="#E8E6F8" strokeWidth="6" />
            <motion.circle cx="42" cy="42" r="38" fill="none" stroke="#66BB6A" strokeWidth="6"
              strokeLinecap="round" strokeDasharray={circ}
              animate={{ strokeDashoffset: offset }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-xl font-heading font-black text-[#6C63FF]">{pct ?? 0}%</span>
          </div>
        </div>
        <h3 className="text-sm font-heading font-bold text-[#1A1A2E] mb-1">Computation</h3>
        <p className="text-xs text-[#6B7280] font-body leading-relaxed">
          {msg || 'Our CA team is processing your request. This step is handled by our experts and will be completed shortly.'}
        </p>
      </div>
    </div>
  );
}

// ── Main ComputationPanel ─────────────────────────────────────────────────
export default function ComputationPanel({ orderId, order, onRefresh }) {
  const [pct, setPct]       = useState(order?.computation_percentage ?? 0);
  const [msg, setMsg]       = useState(order?.computation_message ?? '');
  const [saving, setSaving] = useState(false);
  const [sent, setSent]     = useState(false);
  const [error, setError]   = useState('');
  const [log, setLog]       = useState(order?.computation_log || []);

  useEffect(() => {
    if (!order) return;
    setPct(order.computation_percentage ?? 0);
    setMsg(order.computation_message ?? '');
    setLog(order.computation_log || []);
  }, [order]);

  const origPct  = order?.computation_percentage ?? 0;
  const origMsg  = order?.computation_message   ?? '';
  const unchanged = pct === origPct && msg === origMsg;

  const handleSend = async () => {
    setError('');
    setSaving(true);
    try {
      await API.put(`/admin/orders/${orderId}/computation`, { percentage: pct, message: msg });
      setSent(true);
      setTimeout(() => setSent(false), 2500);
      if (onRefresh) await onRefresh();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update computation');
    }
    setSaving(false);
  };

  const circumference = 2 * Math.PI * 38;

  return (
    <div className="space-y-6">
      {/* ── TOP: Current status + Update form side by side ── */}
      <div className="grid lg:grid-cols-2 gap-6">

        {/* Left — Current State */}
        <div className="bg-white rounded-2xl border border-[#E8E6F8] shadow-sm p-6 flex flex-col items-center text-center">
          <p className="text-xs font-heading font-semibold text-[#6B7280] uppercase tracking-widest mb-4">
            Current Status (Live from DB)
          </p>

          <CircleProgress pct={order?.computation_percentage ?? 0} size={160} stroke={12} />

          <div className="mt-4 space-y-2 w-full">
            <span className={`inline-block text-xs font-body font-semibold px-3 py-1 rounded-full ${statusColor(order?.computation_percentage)}`}>
              {statusLabel(order?.computation_percentage)}
            </span>

            {order?.computation_message ? (
              <blockquote className="text-sm text-[#4A4A60] font-body italic bg-[#F5F4FB] rounded-xl px-4 py-3 border-l-4 border-[#6C63FF]/30 text-left">
                "{order.computation_message}"
              </blockquote>
            ) : (
              <p className="text-xs text-[#9CA3AF] font-body">No message set yet</p>
            )}

            {order?.computation_updated_at && (
              <div className="flex items-center justify-center gap-1.5 text-[10px] text-[#9CA3AF] font-body">
                <Clock className="h-3 w-3" />
                Last updated {new Date(order.computation_updated_at).toLocaleString('en-IN')}
              </div>
            )}
          </div>
        </div>

        {/* Right — Update Form */}
        <div className="bg-white rounded-2xl border border-[#E8E6F8] shadow-sm p-6 space-y-4">
          <p className="text-sm font-heading font-bold text-[#1A1A2E]">Update Computation</p>

          {/* Slider + number */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-body text-[#6B7280]">Progress Percentage</label>
              <span className="text-sm font-heading font-bold text-[#6C63FF]">{pct}%</span>
            </div>
            <div className="flex items-center gap-3">
              <input type="range" min={0} max={100} value={pct}
                onChange={e => setPct(Number(e.target.value))}
                className="flex-1 accent-[#6C63FF] h-2 rounded-full" />
              <input type="number" min={0} max={100} value={pct}
                onChange={e => setPct(Math.max(0, Math.min(100, Number(e.target.value))))}
                className="w-16 text-sm font-body text-center border border-[#E8E6F8] rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-[#6C63FF]/50" />
            </div>
            <p className="text-[10px] text-[#9CA3AF] font-body mt-1">
              Client will see: <strong className="text-[#6C63FF]">{pct}%</strong>
            </p>
          </div>

          {/* Message */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-body text-[#6B7280]">Message to Client</label>
              <span className="text-[10px] text-[#9CA3AF] font-body">{msg.length}/500</span>
            </div>
            <textarea rows={4} maxLength={500} value={msg}
              onChange={e => setMsg(e.target.value)}
              placeholder="e.g. We have reviewed your documents. Your tax computation is in progress and will be ready within 24 hours..."
              className="w-full text-sm font-body border border-[#E8E6F8] rounded-xl p-3 resize-none focus:outline-none focus:ring-1 focus:ring-[#6C63FF]/50"
            />
          </div>

          {error && (
            <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 font-body">
              {error}
            </div>
          )}

          <button onClick={handleSend} disabled={saving || unchanged}
            className={`w-full flex items-center justify-center gap-2 py-3 text-sm font-body font-semibold rounded-xl transition-all ${
              sent
                ? 'bg-green-500 text-white'
                : unchanged
                ? 'bg-[#E8E6F8] text-[#9CA3AF] cursor-not-allowed'
                : 'bg-[#6C63FF] hover:bg-[#5B52CC] text-white shadow-md shadow-[#6C63FF]/20'
            }`}
          >
            {saving ? (
              <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Sending…</>
            ) : sent ? (
              '✓ Sent to Client Successfully'
            ) : (
              <><Send className="h-4 w-4" /> Send Update to Client</>
            )}
          </button>

          {unchanged && !sent && (
            <p className="text-[10px] text-[#9CA3AF] font-body text-center">
              Change the percentage or message to enable update
            </p>
          )}
        </div>
      </div>

      {/* ── Client Preview ── */}
      <ClientPreview pct={pct} msg={msg} />

      {/* ── Computation History ── */}
      {log.length > 0 && (
        <div>
          <p className="text-sm font-heading font-bold text-[#1A1A2E] mb-3">Computation History</p>
          <div className="bg-white rounded-2xl border border-[#E8E6F8] shadow-sm overflow-hidden">
            <table className="w-full text-xs font-body">
              <thead>
                <tr className="bg-[#F5F4FB] border-b border-[#E8E6F8]">
                  {['Date / Time', 'Updated By', 'Change', 'Message Sent'].map(h => (
                    <th key={h} className="px-4 py-3 text-left font-heading font-semibold text-[#6B7280] whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E8E6F8]">
                {log.map((entry, i) => (
                  <tr key={i} className="bg-white hover:bg-[#F5F4FB]">
                    <td className="px-4 py-3 text-[#9CA3AF] whitespace-nowrap">
                      {new Date(entry.updated_at).toLocaleString('en-IN')}
                    </td>
                    <td className="px-4 py-3 font-medium text-[#1A1A2E]">{entry.updated_by_name || 'Admin'}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="text-[#9CA3AF]">{entry.old_percentage ?? '–'}%</span>
                      <span className="mx-1 text-[#9CA3AF]">→</span>
                      <span className="font-bold text-[#6C63FF]">{entry.new_percentage}%</span>
                    </td>
                    <td className="px-4 py-3 text-[#4A4A60] max-w-[240px] truncate italic">
                      {entry.new_message || '–'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
